import styles from 'pages/authorized/repairs/Repairs.module.scss';
import React from 'react';
import EuroIcon from 'assets/icons/euro-icon.svg?react';
import SearchIcon from 'assets/icons/search-white.svg?react';
import ShipmentIcon from 'assets/icons/shipment-icon.svg?react';
import WrenchIcon from 'assets/icons/wrench-icon.svg?react';
import IntakeIcon from 'assets/icons/intake-icon.svg?react';
import { FormStatus } from 'pages/authorized/repairs/Repairs.tsx';

export type Status = {
  key: string;
  className: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  additionalNames?: string[];
  testValue?: string;
};

export const Erfasst = 'Erfasst';
export const VorgangErstellt = 'Vorgang erstellt';
export const SendungIstEingetroffen = 'Sendung ist eingetroffen';
export const AufnahmeIstErfolgt = 'Aufnahme ist erfolgt';
export const BefundungLaeuft = 'Befundung läuft';
export const KVVersendet = 'KV versendet';
export const SKVMAHN1 = 'S-KVMAHN1';
export const SKVMAHN2 = 'S-KVMAHN2';
export const SKVMAHN3 = 'S-KVMAHN3';
export const KVGenehmigt = 'KV genehmigt';
export const InBearbeitung = 'in Bearbeitung';
export const ReparaturAbgeschlossen = 'Reparatur abgeschlossen';
export const WareVersandbereit = 'Ware versandbereit';
export const WareVersendet = 'Ware versendet';

export const KV1Mahnung = 'KV 1. Mahnung';
export const KV2Mahnung = 'KV 2. Mahnung';
export const KV3Mahnung = 'KV 3. Mahnung';
export const CCMussZuDiesemAuftragEtwasKlaeren =
  'CC muss zu diesem Auftrag etwas klären';
export const KVTechnischOk = 'KV technisch  ok';
export const KulanzGeklaert = 'Kulanz geklärt';
export const TechnischeKlaerungME = 'Technische Klaerung ME';
export const TechnischeKlaerungLCM = 'Technische Klärung LCM';
export const TechnischeKlaerungFrei = 'Technische Klärung frei';
export const VorkommnisGeraetPruefenDurchQM =
  'Vorkommnis Gerät prüfen durch QM';
export const VorkommnisFreigegebenDurchQM = 'Vorkommnis freigegeben durch QM';

export const statuses: Record<string, Status> = {
  [Erfasst]: {
    key: 'entered',
    className: styles.blueContainer,
    Icon: IntakeIcon,
    additionalNames: [VorgangErstellt],
    testValue: '1',
  },
  [SendungIstEingetroffen]: {
    key: 'shipment_arrived',
    className: styles.blueContainer,
    Icon: IntakeIcon,
    testValue: '2',
  },
  [AufnahmeIstErfolgt]: {
    key: 'intake_completed',
    className: styles.blueContainer,
    Icon: IntakeIcon,
    testValue: '3',
  },
  [BefundungLaeuft]: {
    key: 'inspection_in_progress',
    className: styles.blueContainer,
    Icon: SearchIcon,
    testValue: '4',
  },
  [KVVersendet]: {
    key: 'cost_estimate_sent',
    className: styles.orangeContainer,
    Icon: EuroIcon,
    testValue: '5',
  },
  [SKVMAHN1]: {
    key: 'rem1',
    className: styles.orangeContainer,
    Icon: EuroIcon,
    additionalNames: [KV1Mahnung],
    testValue: '6',
  },
  [SKVMAHN2]: {
    key: 'rem2',
    className: styles.orangeContainer,
    Icon: EuroIcon,
    additionalNames: [KV2Mahnung],
    testValue: '7',
  },
  [SKVMAHN3]: {
    key: 'final_rem',
    className: styles.orangeContainer,
    Icon: EuroIcon,
    additionalNames: [KV3Mahnung],
    testValue: '8',
  },
  [KVGenehmigt]: {
    key: 'cost_estimate_approved',
    className: styles.blueContainer,
    Icon: EuroIcon,
    testValue: '9',
  },
  [InBearbeitung]: {
    key: 'repair_in_progress',
    className: styles.blueContainer,
    Icon: WrenchIcon,
    additionalNames: [
      CCMussZuDiesemAuftragEtwasKlaeren,
      KulanzGeklaert,
      TechnischeKlaerungME,
      TechnischeKlaerungLCM,
      TechnischeKlaerungFrei,
      VorkommnisGeraetPruefenDurchQM,
      VorkommnisFreigegebenDurchQM,
    ],
    testValue: '10',
  },
  [ReparaturAbgeschlossen]: {
    key: 'repair_completed',
    className: styles.greenContainer,
    Icon: WrenchIcon,
    testValue: '11',
  },
  [WareVersandbereit]: {
    key: 'ready_for_shipment',
    className: styles.greenContainer,
    Icon: ShipmentIcon,
    testValue: '12',
  },
  [WareVersendet]: {
    key: 'goods_shipped',
    className: styles.greenContainer,
    Icon: ShipmentIcon,
    testValue: '13',
  },
};

export const getStatus = (key: string) => {
  return (
    statuses[key] ??
    Object.values(statuses).find((status) =>
      status.additionalNames?.includes(key),
    ) ??
    null
  );
};

export const getStatusValues = (selectedStatuses: FormStatus[]) => {
  const res: string[] = [];

  for (const status of selectedStatuses) {
    res.push(status.value);

    const additionalNames = statuses[status.value]?.additionalNames ?? [];
    res.push(...additionalNames);
  }

  return res.join('_');
};

export const allStatusFilters: FormStatus[] = Object.entries(statuses).map(
  ([key, value]) => ({
    label: value.key,
    value: key,
  }),
);

export const mapStatusQueryParamToForm = (
  statusQueryParam?: string | null,
): FormStatus[] => {
  if (!statusQueryParam) {
    return [];
  }

  const selectedValues = statusQueryParam
    .split('_')
    .map((x) => x.trim())
    .filter(Boolean);

  if (!selectedValues.length) {
    return [];
  }

  const selectedSet = new Set(selectedValues);
  return allStatusFilters.filter((status) => {
    if (selectedSet.has(status.value)) {
      return true;
    }

    const additionalNames = statuses[status.value]?.additionalNames ?? [];
    return additionalNames.some((name) => selectedSet.has(name));
  });
};

export const costEstimateNeedsApprovalStatuses = [
  KVVersendet,
  SKVMAHN1,
  SKVMAHN2,
  SKVMAHN3,
  KV1Mahnung,
  KV2Mahnung,
  KV3Mahnung,
];

export const costEstimateApprovedStatuses = [
  KVGenehmigt,
  InBearbeitung,
  CCMussZuDiesemAuftragEtwasKlaeren,
  KVTechnischOk,
  KulanzGeklaert,
  TechnischeKlaerungME,
  TechnischeKlaerungLCM,
  TechnischeKlaerungFrei,
  VorkommnisGeraetPruefenDurchQM,
  VorkommnisFreigegebenDurchQM,
  ReparaturAbgeschlossen,
  WareVersandbereit,
  WareVersendet,
];
